import React, { useEffect, useState } from 'react';
import './App.css';

function App() {
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('.observe-section');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  const scrollToIntro = () => {
    document.getElementById('intro')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Language Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <button 
          onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
          className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-all duration-300 border border-white/30 flex items-center gap-2"
        >
          <img 
            src={language === 'en' ? 'https://flagcdn.com/w20/fr.png' : 'https://flagcdn.com/w20/gb.png'} 
            alt={language === 'en' ? 'French Flag' : 'British Flag'}
            className="w-5 h-4"
          />
          <span>{language === 'en' ? 'FR' : 'EN'}</span>
        </button>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{backgroundImage: 'url("/images/hero-background.jpg")', backgroundSize: 'cover', backgroundPosition: 'center'}}>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-purple-900/50 to-black/70"></div>
        
        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
          <img src="/images/logo.png" alt="SolveMindPuzzles Logo" className="w-32 h-32 md:w-48 md:h-48 mx-auto mb-8 animate-fade-in" />
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 ">
            {language === 'en' ? 'Unlock Your' : 'Libérez Votre'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">{language === 'en' ? 'Cognitive Potential' : 'Potentiel Cognitif'}</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
            {language === 'en' ? 'The Future of Mobile Brain Training is Here!' : "L'Avenir de l'Entraînement Cérébral Mobile est Ici!"}
          </p>
          
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            {language === 'en' 
              ? 'Welcome to solvemindpuzzles.com, your personal gym for the mind. Discover a universe of brain training games meticulously designed to challenge, entertain, and sharpen your thinking—anytime, anywhere.'
              : 'Bienvenue sur solvemindpuzzles.com, votre salle de sport personnelle pour l\'esprit. Découvrez un univers de jeux d\'entraînement cérébral méticuleusement conçus pour défier, divertir et aiguiser votre réflexion—à tout moment, n\'importe où.'}
          </p>
          
          <p className="text-lg text-gray-200 mb-12 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Dive into an ever-expanding library of puzzles, logic challenges, memory boosters, and strategic games, all crafted to enhance your mental agility right from your device.'
              : 'Plongez dans une bibliothèque en constante expansion de puzzles, défis logiques, boosters de mémoire et jeux stratégiques, tous conçus pour améliorer votre agilité mentale directement depuis votre appareil.'}
          </p>

          <p className="text-xl text-white font-semibold mb-12">
            {language === 'en'
              ? 'Start your adventure in smarter thinking today and experience the thrill of a mind powered up.'
              : 'Commencez votre aventure vers une pensée plus intelligente aujourd\'hui et ressentez le frisson d\'un esprit dynamisé.'}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={scrollToIntro}
              className="px-8 py-4 bg-gradient-to-r from-primary to-secondary rounded-full text-white font-semibold text-lg hover:scale-105 transform transition-all duration-300 shadow-lg hover:shadow-primary/50"
            >
              {language === 'en' ? 'Learn More' : 'En Savoir Plus'}
            </button>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white ">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Introduction Section */}
      <section id="intro" className="observe-section py-20 px-6 bg-gradient-to-b from-black/50 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            {language === 'en' ? 'Your Gateway to' : 'Votre Portail vers'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">{language === 'en' ? 'Smarter Gaming' : 'des Jeux Plus Intelligents'}</span>
          </h2>
          
          <p className="text-lg text-gray-300 mb-8 leading-relaxed">
            {language === 'en'
              ? 'Get ready to elevate your mental fitness with engaging and rewarding cognitive games. At solvemindpuzzles.com, we believe that brain training should be as exciting as it is effective. We deliver a world of premium games, innovative cognitive tools, and mentally stimulating content straight to your screen.'
              : 'Préparez-vous à élever votre forme mentale avec des jeux cognitifs engageants et gratifiants. Chez solvemindpuzzles.com, nous croyons que l\'entraînement cérébral doit être aussi excitant qu\'efficace. Nous livrons un monde de jeux premium, d\'outils cognitifs innovants et de contenu mentalement stimulant directement sur votre écran.'}
          </p>
          
          <p className="text-lg text-gray-300 mb-8">
            {language === 'en'
              ? 'Challenge yourself to think faster, stay focused, and sharpen your memory with an experience that is built for both fun and focus.'
              : 'Défiez-vous de penser plus vite, de rester concentré et d\'aiguiser votre mémoire avec une expérience conçue pour le plaisir et la concentration.'}
          </p>
        </div>
      </section>

      {/* Game Showcase Section */}
      <section id="games" className="observe-section py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">
            {language === 'en' ? 'A Universe of' : 'Un Univers de'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary">{language === 'en' ? 'Brain Training Games' : 'Jeux d\'Entraînement Cérébral'}</span>
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="relative group overflow-hidden rounded-2xl">
              <img src="/images/game-memory.jpg" alt="Memory Game" className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
                <div>
                  <h3 className="text-white font-bold text-xl mb-2">{language === 'en' ? 'Memory Masters' : 'Maîtres de la Mémoire'}</h3>
                  <p className="text-gray-300 text-sm">{language === 'en' ? 'Boost your recall with fun memory challenges' : 'Boostez votre mémoire avec des défis amusants'}</p>
                </div>
              </div>
            </div>
            
            <div className="relative group overflow-hidden rounded-2xl">
              <img src="/images/game-logic.jpg" alt="Logic Puzzle" className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
                <div>
                  <h3 className="text-white font-bold text-xl mb-2">{language === 'en' ? 'Logic Puzzles' : 'Puzzles Logiques'}</h3>
                  <p className="text-gray-300 text-sm">{language === 'en' ? 'Sharpen your reasoning skills' : 'Aiguisez vos compétences de raisonnement'}</p>
                </div>
              </div>
            </div>
            
            <div className="relative group overflow-hidden rounded-2xl">
              <img src="/images/game-math.jpg" alt="Math Challenge" className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
                <div>
                  <h3 className="text-white font-bold text-xl mb-2">{language === 'en' ? 'Math Challenges' : 'Défis Mathématiques'}</h3>
                  <p className="text-gray-300 text-sm">{language === 'en' ? 'Exercise your numerical thinking' : 'Exercez votre pensée numérique'}</p>
                </div>
              </div>
            </div>
            
            <div className="relative group overflow-hidden rounded-2xl">
              <img src="/images/game-pattern.jpg" alt="Pattern Recognition" className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
                <div>
                  <h3 className="text-white font-bold text-xl mb-2">{language === 'en' ? 'Pattern Recognition' : 'Reconnaissance de Motifs'}</h3>
                  <p className="text-gray-300 text-sm">{language === 'en' ? 'Train your visual intelligence' : 'Entraînez votre intelligence visuelle'}</p>
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-lg text-gray-300 text-center mt-12">
            {language === 'en'
              ? 'Dive into puzzles, logic challenges, memory boosters, and strategic games designed for real results.'
              : 'Plongez dans des puzzles, défis logiques, boosters de mémoire et jeux stratégiques conçus pour des résultats réels.'}
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="observe-section py-20 px-6 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">
            {language === 'en' ? 'Engineered for an' : 'Conçu pour une'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-accent">{language === 'en' ? 'Effortless Experience' : 'Expérience Sans Effort'}</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{language === 'en' ? 'Fast & Easy Sign-Up' : 'Inscription Rapide et Facile'}</h3>
              <p className="text-gray-300">{language === 'en' ? 'Begin your brain training journey in seconds. With our streamlined email and mobile verification, you\'re just moments away from unlocking your first challenge. No complicated processes, just instant access.' : 'Commencez votre parcours d\'entraînement cérébral en quelques secondes. Avec notre vérification simplifiée par email et mobile, vous n\'êtes qu\'à quelques instants de débloquer votre premier défi. Pas de processus compliqués, juste un accès instantané.'}</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-secondary to-accent rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{language === 'en' ? 'Train Anywhere, on Any Device' : 'Entraînez-vous Partout, sur Tout Appareil'}</h3>
              <p className="text-gray-300">{language === 'en' ? 'Your mental workout follows you. Enjoy seamless access to your favorite cognitive games on Android, iOS, or Desktop. Your progress syncs automatically, so you can train your mind wherever life takes you.' : 'Votre entraînement mental vous suit. Profitez d\'un accès transparent à vos jeux cognitifs préférés sur Android, iOS ou Desktop. Votre progression se synchronise automatiquement, vous pouvez donc entraîner votre esprit où que la vie vous mène.'}</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{language === 'en' ? 'Smart, Intuitive Design' : 'Design Intelligent et Intuitif'}</h3>
              <p className="text-gray-300">{language === 'en' ? 'Navigate with ease through a clean, personalized hub built for focused mental exercise. Our simple interface eliminates distractions, allowing you to immerse yourself fully in the games that challenge and inspire you most.' : 'Naviguez facilement à travers un hub propre et personnalisé conçu pour un exercice mental concentré. Notre interface simple élimine les distractions, vous permettant de vous immerger pleinement dans les jeux qui vous défient et vous inspirent le plus.'}</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-secondary to-primary rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{language === 'en' ? 'Social & Engaging' : 'Social et Engageant'}</h3>
              <p className="text-gray-300">{language === 'en' ? 'Make brain training a shared adventure! Challenge friends and family to friendly competitions and share your achievements. Discover that getting smarter is more fun together.' : 'Faites de l\'entraînement cérébral une aventure partagée ! Défiez vos amis et votre famille dans des compétitions amicales et partagez vos réalisations. Découvrez que devenir plus intelligent est plus amusant ensemble.'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="observe-section py-20 px-6 bg-black/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">
            {language === 'en' ? 'The SolveMindPuzzles.com' : 'La Différence SolveMindPuzzles.com'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">{language === 'en' ? 'Difference' : ''}</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="w-2 h-8 bg-gradient-to-b from-primary to-secondary mr-4"></span>
                {language === 'en' ? 'Premium, Curated Content' : 'Contenu Premium et Sélectionné'}
              </h3>
              <p className="text-gray-300 ml-6">{language === 'en' ? 'Gain instant access to a wide range of high-quality brain training games tailored to your region. Every game is optimized for a smooth, responsive mobile experience.' : 'Obtenez un accès instantané à une large gamme de jeux d\'entraînement cérébral de haute qualité adaptés à votre région. Chaque jeu est optimisé pour une expérience mobile fluide et réactive.'}</p>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="w-2 h-8 bg-gradient-to-b from-secondary to-accent mr-4"></span>
                {language === 'en' ? 'Personalized Growth' : 'Croissance Personnalisée'}
              </h3>
              <p className="text-gray-300 ml-6">{language === 'en' ? 'Our platform is designed around you. Bookmark your favorite cognitive games, track your performance with insightful progress reports, and tackle challenges that adapt and grow with your skills.' : 'Notre plateforme est conçue autour de vous. Marquez vos jeux cognitifs préférés, suivez vos performances avec des rapports de progression perspicaces et relevez des défis qui s\'adaptent et grandissent avec vos compétences.'}</p>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="w-2 h-8 bg-gradient-to-b from-accent to-primary mr-4"></span>
                {language === 'en' ? 'More Than Just Games' : 'Plus que de Simples Jeux'}
              </h3>
              <p className="text-gray-300 ml-6">{language === 'en' ? 'Explore a suite of top-rated tools and resources designed to boost your daily focus, enhance memory recall, and supercharge your productivity.' : 'Explorez une suite d\'outils et de ressources de premier plan conçus pour stimuler votre concentration quotidienne, améliorer votre mémoire et surcharger votre productivité.'}</p>
            </div>

            <div>
              <div className="bg-gradient-to-r from-primary to-secondary p-8 rounded-2xl">
                <h3 className="text-2xl font-bold text-white mb-4">{language === 'en' ? 'The Success Formula' : 'La Formule du Succès'}</h3>
                <p className="text-3xl font-mono text-white text-center">
                  {language === 'en' ? 'Brainpower = (Focus + Memory) × Consistency' : 'Puissance Cérébrale = (Focus + Mémoire) × Constance'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section id="what-you-get" className="observe-section py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">
            {language === 'en' ? 'What You' : 'Ce Que Vous'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">{language === 'en' ? 'Get' : 'Obtenez'}</span>
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-3">{language === 'en' ? 'A Universe of Games' : 'Un Univers de Jeux'}</h3>
              <p className="text-gray-300 text-sm">{language === 'en' ? 'Dive into puzzles, logic challenges, and memory boosters designed for real results.' : 'Plongez dans des puzzles, défis logiques et boosters de mémoire conçus pour des résultats réels.'}</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-3">{language === 'en' ? 'Train On Your Terms' : 'Entraînez-vous à Vos Conditions'}</h3>
              <p className="text-gray-300 text-sm">{language === 'en' ? 'Play on any device—phone, tablet, or desktop. Your progress syncs everywhere.' : 'Jouez sur n\'importe quel appareil—téléphone, tablette ou ordinateur. Votre progression se synchronise partout.'}</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-3">{language === 'en' ? 'Track Your Growth' : 'Suivez Votre Croissance'}</h3>
              <p className="text-gray-300 text-sm">{language === 'en' ? 'Watch your skills improve with personalized progress tracking and adaptive challenges.' : 'Regardez vos compétences s\'améliorer avec un suivi de progression personnalisé et des défis adaptatifs.'}</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-3">{language === 'en' ? 'Instant Start' : 'Démarrage Instantané'}</h3>
              <p className="text-gray-300 text-sm">{language === 'en' ? 'Sign up in seconds with simple email and phone verification and start playing immediately.' : 'Inscrivez-vous en quelques secondes avec une simple vérification par email et téléphone et commencez à jouer immédiatement.'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="observe-section py-20 px-6 bg-black/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            {language === 'en' ? 'About' : 'À Propos de'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">solvemindpuzzles.com</span>
          </h2>
          
          <p className="text-lg text-gray-300 leading-relaxed mb-8">
            {language === 'en'
              ? 'solvemindpuzzles.com is a premier digital platform dedicated to making cognitive fitness accessible and enjoyable for everyone. Our mission is to deliver top-quality brain training games and tools straight to your device, offering a powerful and engaging mental workout on your schedule.'
              : 'solvemindpuzzles.com est une plateforme numérique de premier plan dédiée à rendre la forme cognitive accessible et agréable pour tous. Notre mission est de fournir des jeux et outils d\'entraînement cérébral de haute qualité directement sur votre appareil, offrant un entraînement mental puissant et engageant selon votre emploi du temps.'}
          </p>
          
          <p className="text-lg text-gray-300 leading-relaxed">
            {language === 'en'
              ? 'We empower you to customize your game libraries, set personal cognitive goals, and embark on a training journey that is truly your own.'
              : 'Nous vous donnons le pouvoir de personnaliser vos bibliothèques de jeux, de définir des objectifs cognitifs personnels et de vous lancer dans un parcours d\'entraînement qui vous est vraiment propre.'}
          </p>
        </div>
      </section>

      {/* Why solvemindpuzzles.com Section */}
      <section id="why-platform" className="observe-section py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            {language === 'en' ? 'Why' : 'Pourquoi'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary">solvemindpuzzles.com?</span>
          </h2>
          
          <p className="text-lg text-gray-300 leading-relaxed">
            {language === 'en'
              ? 'We provide a premium, ad-free experience focused on one thing: making you sharper. Our intuitive design and curated library of games make it easy and enjoyable to invest in your mental fitness every day.'
              : 'Nous offrons une expérience premium sans publicité axée sur une chose : vous rendre plus vif. Notre design intuitif et notre bibliothèque de jeux sélectionnés rendent facile et agréable d\'investir dans votre forme mentale chaque jour.'}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="observe-section py-20 px-6 bg-black/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            {language === 'en' ? 'Connect With Us' : 'Contactez-Nous'}
          </h2>
          
          <p className="text-lg text-gray-300 mb-8">
            {language === 'en'
              ? 'Have a question about our brain training games, your account, or our features? We recommend visiting our comprehensive FAQ page for quick, detailed answers.'
              : 'Vous avez une question sur nos jeux d\'entraînement cérébral, votre compte ou nos fonctionnalités ? Nous vous recommandons de visiter notre page FAQ complète pour des réponses rapides et détaillées.'}
          </p>
          
          <p className="text-lg text-gray-300 mb-8">
            {language === 'en'
              ? 'For all other inquiries, our support team is ready to help. We are also excited to connect with innovators and explore smart partnerships.'
              : 'Pour toutes autres demandes, notre équipe de support est prête à vous aider. Nous sommes également ravis de nous connecter avec des innovateurs et d\'explorer des partenariats intelligents.'}
          </p>
          
          <p className="text-lg text-gray-200 mb-8">
            {language === 'en'
              ? 'Feel free to reach out anytime at:'
              : 'N\'hésitez pas à nous contacter à tout moment :'}
          </p>
          
          <div className="mb-12">
            <a href="mailto:help@solvemindpuzzles.com" className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary to-secondary rounded-full text-white font-semibold text-lg hover:scale-105 transform transition-all duration-300 shadow-lg hover:shadow-primary/50">
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              help@solvemindpuzzles.com
            </a>
          </div>
          
          <h3 className="text-2xl font-bold text-white mb-6">
            {language === 'en' ? 'Contact & Partnerships' : 'Contact et Partenariats'}
          </h3>
          
          <p className="text-lg text-gray-300">
            {language === 'en'
              ? 'Questions? See our FAQ or email us at help@solvemindpuzzles.com. We\'re always open to smart collaborations.'
              : 'Des questions ? Consultez notre FAQ ou écrivez-nous à help@solvemindpuzzles.com. Nous sommes toujours ouverts aux collaborations intelligentes.'}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-black/50">
        <div className="max-w-6xl mx-auto">
          {/* Service Details Section */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-white/10">
            {language === 'en' ? (
              <>
                <h3 className="text-2xl font-bold text-white mb-4">Service Details</h3>
                <p className="text-gray-300 mb-4">
                  Enjoy the service by sending ACCESS to 8900. The MindFit service includes cognitive training, 
                  with mindfulness sessions and community support for a holistic approach to mental well-being. 
                  You will have access to 50 online games, unlimited for 3 months.
                </p>
                <p className="text-gray-300 mb-4">
                  The cost of access to the service is €3.5 gross (VAT included).
                </p>
                <p className="text-gray-300">
                  For support, contact 0171255555 or customer-support@allopass.com
                </p>
              </>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-white mb-4">Détails du Service</h3>
                <p className="text-gray-300 mb-4">
                  Profitez du service en envoyant ACCESS au 8900. Le service MindFit comprend un entraînement cognitif, 
                  avec des séances de pleine conscience et un soutien communautaire pour une approche holistique du bien-être mental. 
                  Vous disposerez de 50 jeux en ligne, de façon illimitée pendant 3 mois.
                </p>
                <p className="text-gray-300 mb-4">
                  Le coût de l'accès au service est de 3,5 € bruts (TVA incluse).
                </p>
                <p className="text-gray-300">
                  Pour le support, joignez le 0171255555 ou customer-support@allopass.com
                </p>
              </>
            )}
          </div>

          {/* Copyright */}
          <div className="text-center">
            <p className="text-gray-400">© 2024 SolveMindPuzzles.com. {language === 'en' ? 'All rights reserved.' : 'Tous droits réservés.'}</p>
            <p className="text-gray-500 text-sm mt-2">
              {language === 'en' ? 'Power up your brain. Play smarter.' : 'Boostez votre cerveau. Jouez plus intelligemment.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
